import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators, compose } from "redux";
import { withFormik } from "formik";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { Grid } from "@material-ui/core";
import { InputText, InputTypeAhead, PrimaryButton, SecondaryButton } from "@loanbossinc/component-library";

import { PaddedWrapper, Row } from "../styles";
import HousesIcon from "../../../houses.svg";

import {
  DescriptionWrapper,
  SectionWrapper,
  LeftSectionWrapper,
  HousesIconWrapper,
  InputWrapper,
  RightSectionWrapper,
  ControlRow,
  ControlRowItem
} from "./calculatedFieldStyles";
import { FieldSelector } from "./fieldSelector";
import { HardCodedCalculationField } from "./hardCodedCalculationField";
import { PredefinedCalculationField } from "./predefinedCalculationField";
import PreviewBar from "./previewBar";
import { actionCreators } from "./calculatedField.store";

const applyOperation = (val1, val2, operation) => {
  switch (operation) {
    case "addition":
      return val1 + val2;
    case "subtraction":
      return val1 - val2;
    case "multiplication":
      return val1 * val2;
    case "division":
      return val1 / val2;
    default:
      return null;
  }
};

const calculatePemdas = expressionString => {
  const operators = {
    "multiplication": (x, y) => x * y,
    "division": (x, y) => x / y,
    "subtraction": (x, y) => x - y,
    "addition": (x, y) => x + y
  };

  const mapping = Object.entries(operators).reverse();

  const calculate = (
    expression,
    [current, ...remaining] = mapping
  ) => {
    if (!current) {
      return Number(expression);
    }

    const [operator, fn] = current;
    const terms = expression.trim().split(operator);

    return terms.map(term => calculate(term, remaining)).reduce(fn);
  };

  return calculate(expressionString);
};

export const calculateResult = calculations => {
  const minNumberOfCalculations = 2;
  if (calculations.length < minNumberOfCalculations) {
    return null;
  }

  const expressionString = calculations.reduce((acc, curr, idx) => {
    if (idx === calculations.length - 1) {
      return `${acc}${curr.value}`;
    }

    return `${acc}${curr.value}${curr.operation}`;
  }, "");

  return calculatePemdas(expressionString);
};

const hardCodedType = "hard-coded";

export const CalculatedField = props => {
  const {
    deal,
    fields,
    handleSubmit,
    isSelectingField,
    selectedCalculation,
    setCalculatedFieldSelections,
    setFieldValue,
    setFieldSelections,
    values,
    previewValue,
    setCalculatedFieldsToDefault,
    fieldsFilter,
    isEditingLineItem,
    setDrawerSelections
  } = props;

  useEffect(
    () => () => {
      setCalculatedFieldsToDefault();
    },
    []
  );

  const { name = "", level = null, propertyId = null, calculations = [] } = values;

  useEffect(
    () => {
      const updatedCalcs = calculations.map(calculation => {
        const calculationSourceField = fields.find(f => f.id === calculation.fieldId);
        if(calculationSourceField && calculationSourceField.value !== calculation.value) {
          return { ...calculation, formattedValue: calculationSourceField.value, value: parseFloat(calculationSourceField.value) };
        }
        return calculation;
      });
      setFieldValue("calculations", updatedCalcs);
      const result = calculateResult(updatedCalcs);
      setCalculatedFieldSelections({ previewValue: result });
    },
  []);

  const onCancel = () => {
    setFieldSelections({ currentSelectedFieldType: null, isAdding: false, isEditing: false, selectedField: null });
    if (isEditingLineItem) {
      setDrawerSelections({ isEditingLineItem: true, currentSectionName: "line-item-management" });
    }
  };

  useEffect(() => {
    if (fieldsFilter && fieldsFilter !== "0") {
      setFieldValue("propertyId", parseInt(fieldsFilter, 10));
      setFieldValue("level", "property");
    }
  },
  [fieldsFilter]);

  const onPropertySelected = value => {
    setFieldValue("propertyId", value);

    if (value !== -1) {
      setFieldValue("level", "property");
    } else {
      setFieldValue("level", "deal");
    }
  };

  const onValueChange = (event, item) => {
    const {
      target: { value }
    } = event;

    const updatedCalcs = calculations.map(x => {
      if (x.id === item.id) {
        return { ...x, formattedValue: value, value: parseFloat(value) };
      }

      return x;
    });

    setFieldValue("calculations", updatedCalcs);
  };

  const onSetOperation = (item, operation) => {
    const updatedCalcs = calculations.map(x => {
      if (x.id === item.id) {
        return { ...x, operation };
      }

      return x;
    });

    setFieldValue("calculations", updatedCalcs);
  };

  const onNameChange = event => {
    const {
      target: { value }
    } = event;

    setFieldValue("name", value);
  };

  const onAddField = () => {
    setFieldValue("calculations", [...calculations, { id: uuidv4(), index: calculations.length, type: "field", fieldId: null, operation: null, value: null }]);
  };

  const onAddHardCodedValue = () => {
    setFieldValue("calculations", [
      ...calculations,
      { id: uuidv4(), index: calculations.length, type: hardCodedType, formattedValue: null, value: null, operation: null }
    ]);
  };

  const onSelectNewField = item => {
    setFieldSelections({ isSelectingField: true });
    setCalculatedFieldSelections({ selectedCalculation: item });
  };

  const onRemoveCalculation = calculationToRemove => {
    const calculationIndex = calculationToRemove.index;
    const calculationId = calculationToRemove.id;

    const updatedCalculations = calculations
      .filter(x => x.id !== calculationId)
      .map(y => {
        if (y.index > calculationIndex) {
          return { ...y, index: y.index - 1 };
        }

        return y;
      });

    setFieldValue("calculations", updatedCalculations);
  };

  const hasInvalidCalculations =
    Boolean(calculations) &&
    Boolean(calculations.find(
      (x, idx) => {
        const operatorNotSelected = idx + 1 !== calculations.length && !x.operation;
        const incorrectValue = x.type === hardCodedType && !x.value && x.value !== 0;

        return (x.type === "field" && !x.fieldId) || incorrectValue || operatorNotSelected;
      }
    ));

  let divisionByZero = false;
  for (let i = 0; i < calculations.length - 1; i++) {
    if (calculations.length > 1 && calculations[i].operation === "division" && calculations[i + 1].value === 0) {
      divisionByZero = true;
      break;
    }
  }

  const areInputsValid = Boolean(name) && Boolean(propertyId) && Boolean(level);
  const areCalculationsValid = !hasInvalidCalculations && Boolean(calculations) && calculations.length > 1 && !divisionByZero;
  const canSave = areInputsValid && areCalculationsValid;

  const propertiesForSourceOptions = _.sortBy(
    Boolean(deal) && Boolean(deal.properties) && deal.properties.map(x => ({ id: x.id, name: x.name || "No Property Name Given" }), ["name"])
  );

  const sourceOptions = [{ id: -1, name: "Deal" }, ...propertiesForSourceOptions];

  const onFieldSelectedForCalculation = (fieldId, value) => {
    const updatedCalcs = calculations.map(x => {
      if (x.id === selectedCalculation.id) {
        return { ...x, fieldId, value };
      }

      return x;
    });

    setFieldValue("calculations", updatedCalcs);

    setFieldSelections({ isSelectingField: false });
    setCalculatedFieldSelections({ calculatedFieldFieldsFilter: "0", calculatedFieldSearchTerm: "", selectedCalculation: null });
  };

  const calculationFields = _.sortBy(calculations, ["index"]).map((calc, idx) => {
    const rowProps = {};
    if (idx + 1 !== calculations.length) {
      rowProps.borderBottom = "none";
    }

    if (calc.type === hardCodedType) {
      return (
        <HardCodedCalculationField
          key={calc.id}
          calculation={calc}
          idx={idx}
          numberOfCalculations={calculations.length}
          onRemoveCalculation={onRemoveCalculation}
          onSetOperation={onSetOperation}
          onValueChange={onValueChange}
          rowProps={rowProps}
        />
      );
    }

    return (
      <PredefinedCalculationField
        key={calc.id}
        calculation={calc}
        fields={fields}
        idx={idx}
        numberOfCalculations={calculations.length}
        onRemoveCalculation={onRemoveCalculation}
        onSelectNewField={onSelectNewField}
        onSetOperation={onSetOperation}
        rowProps={rowProps}
      />
    );
  });

  return (
    <>
      {!isSelectingField && (
        <>
          <PaddedWrapper padding="1em">
            <InputText variant="filled" label="Field Name" name="field-name" fullWidth onChange={onNameChange} value={name} />
          </PaddedWrapper>

          <SectionWrapper>
            <LeftSectionWrapper>
              <Grid container justify="flex-start" alignItems="center" direction="row">
                <Grid item xs={2}>
                  <HousesIconWrapper>
                    <img src={HousesIcon} alt="Houses Icon" height={40} width={32} />
                  </HousesIconWrapper>
                </Grid>
                <Grid item xs={10}>
                  <DescriptionWrapper id="dscr">Show metrics for the deal or a specific property.</DescriptionWrapper>
                </Grid>
              </Grid>
            </LeftSectionWrapper>
            <RightSectionWrapper>
              <InputWrapper id="level-dd">
                <ControlRow>
                  <ControlRowItem>
                    <InputTypeAhead
                      variant="filled"
                      id="field-level"
                      name="field-level"
                      label="Field level"
                      options={sourceOptions}
                      displayWith={option => option && option.name}
                      setValueWith={option => option && option.id}
                      onChange={onPropertySelected}
                      value={propertyId}
                      style={{ width: "auto" }}
                    />
                  </ControlRowItem>
                </ControlRow>
              </InputWrapper>
            </RightSectionWrapper>
          </SectionWrapper>

          {calculationFields}

          <PreviewBar
            initialValue={props.selectedField?.value || props.selectedField?.value === 0 ? props.selectedField.value : null}
            calculations={calculations}
            areCalculationsValid={areCalculationsValid}
            setCalculatedFieldSelections={setCalculatedFieldSelections}
            previewValue={previewValue}
          />

          <Row justifyContent="space-between">
            <PaddedWrapper>
              <SecondaryButton size="sm" disabled={hasInvalidCalculations || !propertyId} label="Add field" onClick={onAddField} />
              <SecondaryButton
                size="sm"
                disabled={hasInvalidCalculations || !propertyId}
                label="Add hard coded value"
                onClick={onAddHardCodedValue}
                style={{ marginLeft: "1em", width: "250px" }}
              />
            </PaddedWrapper>

            <PaddedWrapper>
              <SecondaryButton size="sm" label="Cancel" onClick={onCancel} />
              <PrimaryButton size="sm" disabled={!canSave} label="Save" onClick={handleSubmit} style={{ marginLeft: "1em" }} />
            </PaddedWrapper>
          </Row>
        </>
      )}

      {isSelectingField && <FieldSelector {...props} propertyId={propertyId} onFieldSelectedForCalculation={onFieldSelectedForCalculation} />}
    </>
  );
};

const mapStateToProps = ({ lenderReportCalculatedField }) => ({
  ...lenderReportCalculatedField
});
const mapDispatchToProps = dispatch => bindActionCreators({ ...actionCreators }, dispatch);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withFormik({
    mapPropsToValues: ({ selectedField }) => ({ ...selectedField, rowsSelected: selectedField?.rowsSelected ?? [] }),
    handleSubmit(values, { props, setSubmitting }) {
      const {
        dealId,
        isAdding,
        postFieldConfig,
        putFieldConfig,
        setFieldSelections,
        changeDrawerStateIfInNewListItemMode,
        isEditingLineItem,
        setNoiCalculationsDashboardSelections,
        setDrawerSelections,
        setNewLineItemsSelectionsToDefault
      } = props;
      const resultValue = calculateResult(values.calculations || []);

      const payload = {
        ...values,
        isCalculated: true,
        type: "calculated",
        value: resultValue,
        dealId,
        formattedValue: `${resultValue}`
      };

      if (isAdding) {
        postFieldConfig({ data: payload });
      } else {
        putFieldConfig({ data: _.omit(payload, ["updatedAt", "createdAt"]) });
      }

      setSubmitting(false);
      changeDrawerStateIfInNewListItemMode();
      setFieldSelections({ currentSelectedFieldType: null, selectedField: null, isAdding: false, isEditing: false });
      if (isEditingLineItem) {
        setNoiCalculationsDashboardSelections({
          initialDrawerSectionName: null,
          isLenderReportingDrawerOpen: false,
          addLineItemType: null,
          lineItemToEdit: null
        });
        setDrawerSelections({ isEditingLineItem: false, currentSectionName: null });
        setNewLineItemsSelectionsToDefault();
      }
    }
  })
)(CalculatedField);
